import { useCallback, useEffect, useRef, useState } from 'react'

import type { FetchLike } from 'eventsource-client'
import { createEventSource } from 'eventsource-client'

import { store } from '@web/app/store'
import { useNotificationActions } from '@web/entities/notification'
import { EAuthStatus } from '@web/shared/enums/auth/auth-status.enum'
import { refreshAuthSession } from '@web/shared/infrastructure/auth-session'
import { useAppSelector } from '@web/shared/utils/use-app-selector.util'

const DEFAULT_PING_TIMEOUT = 10_000

type TSseUrlResolver = string | ((host: string) => string)

export interface ISseMessage {
  data: string
  event?: string
}

interface IUseSseSubscriptionParams {
  url: TSseUrlResolver
  enabled?: boolean
  pingEvent?: string
  pingTimeout?: number
  onConnectionLost?: () => void
  onMessage?: (message: ISseMessage) => void
  onReconnected?: () => void
}

const createFetchWithStatus = (): FetchLike & { status?: string } => {
  const f: FetchLike & { status?: string } = async (url, init) => {
    f.status = undefined
    const response = await fetch(url, init)
    f.status = `${response.status} ${response.statusText}`
    return response
  }

  return f
}

export const useSseSubscription = ({
  url,
  enabled = true,
  pingEvent = 'ping',
  pingTimeout = DEFAULT_PING_TIMEOUT,
  onConnectionLost,
  onMessage,
  onReconnected
}: IUseSseSubscriptionParams) => {
  const [isLoading, setIsLoading] = useState(false)
  const pingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const esRef = useRef<ReturnType<typeof createEventSource> | null>(null)
  const isDisconnectedRef = useRef(true)

  const activeServer = useAppSelector(
    (state) => state.serverSettings.activeServer
  )
  const { accessToken, authStatus, refreshToken } = useAppSelector(
    (state) => state.auth
  )
  const {
    setNotificationError,
    setNotificationIsActive,
    setNotificationMessage
  } = useNotificationActions()

  const notifyDisconnect = useCallback(() => {
    console.error('Ошибка. Обрыв соединения с сервером. Переподключение...')
    setNotificationError(
      'Ошибка. Обрыв соединения с сервером. Переподключение...'
    )
  }, [setNotificationError])

  const notifyReconnect = useCallback(() => {
    setNotificationMessage({
      message: 'Соединение восстановлено',
      type: 'success',
      isLoading: false
    })
    setTimeout(() => {
      setNotificationIsActive(false)
    }, 2000)
  }, [setNotificationIsActive, setNotificationMessage])

  const clearPingTimer = useCallback(() => {
    if (pingTimeoutRef.current) {
      clearTimeout(pingTimeoutRef.current)
      pingTimeoutRef.current = null
    }
  }, [])

  const clearReconnectTimer = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current)
      reconnectTimeoutRef.current = null
    }
  }, [])

  const closeConnection = useCallback(() => {
    clearPingTimer()
    clearReconnectTimer()
    esRef.current?.close()
    esRef.current = null
    setIsLoading(false)
  }, [clearPingTimer, clearReconnectTimer])

  const restartConnection = useCallback(() => {
    clearPingTimer()
    clearReconnectTimer()

    const eventSource = esRef.current
    if (!eventSource) {
      return
    }

    eventSource.close()
    reconnectTimeoutRef.current = setTimeout(() => {
      reconnectTimeoutRef.current = null
      eventSource.connect()
    }, 0)
  }, [clearPingTimer, clearReconnectTimer])

  const connectSse = useCallback(
    (resolvedUrl: string) => {
      if (!accessToken || !refreshToken) return
      if (esRef.current) return

      setIsLoading(true)

      const customFetch = createFetchWithStatus()
      const handleConnectionLost = onConnectionLost ?? notifyDisconnect
      const handleReconnected = onReconnected ?? notifyReconnect

      esRef.current = createEventSource({
        url: resolvedUrl,
        fetch: customFetch,
        headers: { Authorization: `Bearer ${accessToken}` },
        onMessage: ({ data, event }) => {
          onMessage?.({ data, event })

          if (event !== pingEvent) {
            return
          }

          setIsLoading(false)

          if (isDisconnectedRef.current) {
            handleReconnected()
            isDisconnectedRef.current = false
          }

          clearPingTimer()
          pingTimeoutRef.current = setTimeout(() => {
            console.warn('[SSE] ping timeout')
            isDisconnectedRef.current = true
            handleConnectionLost()
            restartConnection()
          }, pingTimeout)
        },
        onDisconnect: async () => {
          if (customFetch.status?.startsWith('401')) {
            await refreshAuthSession(store)
            closeConnection()
          }
        }
      })
    },
    [
      accessToken,
      clearPingTimer,
      closeConnection,
      notifyDisconnect,
      notifyReconnect,
      onConnectionLost,
      onMessage,
      onReconnected,
      pingEvent,
      pingTimeout,
      restartConnection,
      refreshToken
    ]
  )

  useEffect(() => {
    if (!enabled || authStatus !== EAuthStatus.AUTHORIZED) {
      closeConnection()
      return
    }

    const host = activeServer?.url ?? ''
    const resolvedUrl = typeof url === 'function' ? url(host) : `${host}${url}`

    connectSse(resolvedUrl)

    return () => {
      closeConnection()
      isDisconnectedRef.current = true
    }
  }, [activeServer, authStatus, closeConnection, connectSse, enabled, url])

  return { isLoading }
}
